<i18n>
{
  "en": {
    "username": "Username",
    "firstName": "Name",
    "isActive": "Active",
    "manager": "Manager",
    "employee": "Employee",
    "role": "Role of user",
    "roleLevel": "Create user with the same hierarchy level",
    "createUserRoleTitle": "Creating user",
    "editUserRoleTitle": "Editing user",
    "emptyFieldError": "Required field",
    "email": "Email",
    "notValidEmail": "Email is not valid",
    "assignPos": "Assign restaurant",
    "allowedPointsOfSales":  "Select restaurants",
    "availableFrontGroups": "Select modules",
    "important": "Important",
    "errorAllowedPosNotIncluded": "The user has a restaurant assigned which you are not allowed to see, therefore if you do not assign any restaurant the user will disappear."
  },
  "es": {
    "username": "Nombre de usuario",
    "firstName":  "Nombre",
    "isActive":  "Activo",
    "manager":  "Manager",
    "employee":  "Empleado",
    "role":  "Rol del usuario",
    "roleLevel":  "Crear usuario con mismo nivel de jerarquia",
    "createUserRoleTitle": "Creando usuario",
    "editUserRoleTitle": "Editando usuario",
    "emptyFieldError": "Campo obligatorio",
    "email":  "Correo Electronico",
    "notValidEmail":  "El email no es valido",
    "assignPos":  "Asignar restaurante",
    "allowedPointsOfSales":  "Seleccionar restaurantes",
    "availableFrontGroups": "Seleccionar modulos",
    "important":  "Importante",
    "errorAllowedPosNotIncluded":  "El usuario tiene un restaurante asignado el cual usted no tiene permitido ver, por lo tanto si no le asigna ningun restaurante el usuario desaparecera."
  }
}
</i18n>
<template>
  <v-dialog v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if="visible" scrollable persistent class="px-7 py-4">
      <v-card-title class="pa-0 mb-2">
        {{ modalTitle }}
      </v-card-title>
      <v-sheet style="background-color: #ffffff">
        <v-form v-model="valid" ref="form">
          <v-text-field :disabled="type === 'edit'" v-model="user.username" :rules="usernameRules" :label="$t('username')" class="required"/>
          <v-text-field v-model="user.firstName" :rules="firstNameRules" :label="$t('firstName')" class="required"/>
          <v-text-field v-model="user.email" :rules="emailRules" :label="$t('email')" class="required"/>
          <v-checkbox v-model="user.isActive" :label="$t('isActive')"/>
          <v-select v-model="role" :rules="selectRules" :items="roles" :menu-props="{ maxHeight: '400' }" :label="$t('role')"
          item-value="value" item-text="name" class="required"/>
          <v-checkbox v-if="role == 1" v-model="roleLevel" :label="$t('roleLevel')"/>
          <v-select v-model="allowedPointsOfSales" :rules="listRules" :items="pointOfSaleList" :menu-props="{ maxHeight: '400' }"
          :label="$t('allowedPointsOfSales')" item-value="guid" chips multiple item-text="name" class="required"/>
          <div class="error-message-allowed-pod" v-if="userHaveOtherPos">{{ $t('important') }}<br/>{{ $t('errorAllowedPosNotIncluded') }}</div>
          <v-select v-model="availableFrontGroups" :rules="listRules" :items="userSettings.availableFrontGroups" :menu-props="{ maxHeight: '400' }"
          :label="$t('availableFrontGroups')" item-value="guid" item-text="name" multiple chips class="required"/>
          <dynameat-modal-buttons class="mt-3" @cancel="onCancel" @confirm="onConfirm" :confirmDisabled="!valid"/>
        </v-form>
      </v-sheet>
    </v-card>
  </v-dialog>
</template>
<script>
import {
  notEmptyList,
  notEmptyString,
  notValidEmail
} from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Components, MapGetters } from '@core/helpers/decorators';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { UserRole, User } from '@userManager/models';

@Component
@Components({ DynameatModalButtons })
@MapGetters(['pointOfSaleList', 'userSettings'])
export default class UserRoleCreateEditModal extends Vue {
  @Prop({
    Object,
    validator(userRole) {
      return userRole instanceof UserRole;
    }
  }) userRole;

  @Prop({
    String,
    default: 'create',
    validator(type) {
      return type === 'create' || type === 'edit';
    }
  }) type;

  @Prop({ Boolean, default: false }) visible;

  role = null;

  roleLevel = false;

  user = new User();

  availableFrontGroups = []

  allowedPointsOfSales = []

  valid = false;

  get modalTitle() {
    return this.$t(`${this.type}UserRoleTitle`);
  }

  get usernameRules() {
    return [notEmptyString(this.$t('emptyFieldError'))];
  }

  get firstNameRules() {
    return [notEmptyString(this.$t('emptyFieldError'))];
  }

  get selectRules() {
    if (!this.role) return [this.$t('emptyFieldError')];
    return [];
  }

  get listRules() {
    return [notEmptyList(this.$t('emptyFieldError'))];
  }

  get emailRules() {
    return [notEmptyString(this.$t('emptyFieldError')), notValidEmail(this.$t('notValidEmail'))];
  }

  get roles() {
    return [
      { value: 1, name: this.$t('manager') },
      { value: 2, name: this.$t('employee') },
    ];
  }

  get userHaveOtherPos() {
    const posGuidList = this.pointOfSaleList.map((each) => each.guid);
    const filterPosIsNotAvailable = this.allowedPointsOfSales.filter((value) => !posGuidList.includes(value));
    const filterPosAvailable = this.allowedPointsOfSales.filter((value) => posGuidList.includes(value));
    return filterPosAvailable.length === 0 && filterPosIsNotAvailable.length > 0;
  }

  clearOwnData() {
    this.role = null;
    this.roleLevel = false;
    this.user = new User();
    this.availableFrontGroups = [];
    this.allowedPointsOfSales = [];
  }

  onCancel() {
    this.clearOwnData();
    this.$emit('cancel');
  }

  onConfirm() {
    if (this.$refs.form.validate()) {
      this.userRole.role = this.role;
      this.userRole.roleLevel = this.handleRoleLevelSend();
      this.userRole.availableFrontGroups = this.availableFrontGroups;
      this.userRole.allowedPointsOfSales = this.allowedPointsOfSales;
      this.userRole.user = this.user;
      this.clearOwnData();
      this.$emit('confirm', this.userRole);
    }
  }

  handleRoleLevelSend() {
    if (this.roleLevel) {
      return this.userSettings.roleLevel;
    }
    return this.userSettings.roleLevel + 1;
  }

  handleRoleLevelReceived(roleLevel) {
    if (roleLevel === null) {
      return false;
    }
    if (roleLevel === this.userSettings.roleLevel) {
      return true;
    }
    return false;
  }

  @Watch('userRole', { immediate: true })
  onUserRoleChanged(newUserRole) {
    if (newUserRole) {
      this.clearOwnData();
      this.role = newUserRole.role;
      this.roleLevel = this.handleRoleLevelReceived(newUserRole.roleLevel);
      this.availableFrontGroups = newUserRole.availableFrontGroups;
      this.allowedPointsOfSales = newUserRole.allowedPointsOfSales;
      this.user = newUserRole.user;
    }
  }
}
</script>
<style scoped>
.error-message-allowed-pod {
  color: red;
  font-size: 12px;
  line-height: 20px;
  padding: 5px 10px 10px 10px;
}
</style>
